import { cardId, RANKS, SUITS, type Card, type CardId } from "./card";

/** Number of cards in a standard deck. */
export const DECK_SIZE = 52;

/**
 * A fresh, ordered 52-card deck: clubs 2→A, then diamonds, hearts, spades.
 * Always returns a new array, so callers may shuffle/deal freely.
 */
export function standardDeck(): Card[] {
  const deck: Card[] = [];
  for (const suit of SUITS) {
    for (const rank of RANKS) {
      deck.push({ suit, rank });
    }
  }
  return deck;
}

/**
 * Assert there are no duplicate cards in a collection. Throws with the
 * offending card id. Used pervasively by the simulation harness to enforce
 * "card conservation" (Section 10).
 */
export function assertNoDuplicates(cards: readonly Card[]): void {
  const seen = new Set<CardId>();
  for (const card of cards) {
    const id = cardId(card);
    if (seen.has(id)) throw new Error(`Duplicate card in collection: ${id}`);
    seen.add(id);
  }
}

/**
 * Assert that the given groups (hands, table, deck, discards, …) together form
 * exactly one full, duplicate-free 52-card deck. This is the canonical
 * conservation invariant every card game must uphold after every move.
 */
export function assertFullDeck(...groups: readonly (readonly Card[])[]): void {
  const all = groups.flat();
  if (all.length !== DECK_SIZE) {
    throw new Error(`Card conservation violated: expected ${DECK_SIZE} cards, found ${all.length}`);
  }
  assertNoDuplicates(all);
}
